// src/Components/Promo/PortfolioPDF.js

import { Buffer } from 'buffer';
import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  pdf,
  Font,
} from '@react-pdf/renderer';
import { Html } from 'react-pdf-html';
import { saveAs } from 'file-saver';

import * as pdfjsLib from 'pdfjs-dist';

// Dynamically link the modern .mjs worker based on the installed version
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.mjs`;

if (typeof window !== 'undefined') {
  window.Buffer = window.Buffer || Buffer;
}

/**
 * ALIASING STRATEGY:
 * To accommodate all fonts in the RichTextEditor dropdown, we map the
 * RTE font names to the built-in PDF standard fonts (Core 14).
 */

Font.register({
  family: 'Arial',
  fonts: [
    { src: 'Helvetica' },
    { src: 'Helvetica-Bold', fontWeight: 'bold' },
    { src: 'Helvetica-Oblique', fontStyle: 'italic' },
    { src: 'Helvetica-BoldOblique', fontWeight: 'bold', fontStyle: 'italic' },
  ],
});

Font.register({
  family: 'Times New Roman',
  fonts: [
    { src: 'Times-Roman' },
    { src: 'Times-Bold', fontWeight: 'bold' },
    { src: 'Times-Italic', fontStyle: 'italic' },
    { src: 'Times-BoldItalic', fontWeight: 'bold', fontStyle: 'italic' },
  ],
});

Font.register({
  family: 'Courier New',
  fonts: [
    { src: 'Courier' },
    { src: 'Courier-Bold', fontWeight: 'bold' },
    { src: 'Courier-Oblique', fontStyle: 'italic' },
    { src: 'Courier-BoldOblique', fontWeight: 'bold', fontStyle: 'italic' },
  ],
});

Font.register({
  family: 'Georgia',
  fonts: [
    { src: 'Times-Roman' },
    { src: 'Times-Bold', fontWeight: 'bold' },
    { src: 'Times-Italic', fontStyle: 'italic' },
  ],
});

Font.register({
  family: 'Verdana',
  fonts: [{ src: 'Helvetica' }, { src: 'Helvetica-Bold', fontWeight: 'bold' }],
});

Font.register({
  family: 'Comic Sans MS',
  fonts: [{ src: 'Helvetica' }],
});

Font.register({
  family: 'Segoe UI',
  fonts: [{ src: 'Helvetica' }, { src: 'Helvetica-Bold', fontWeight: 'bold' }],
});
Font.register({
  family: 'Segoe UI Semibold',
  fonts: [{ src: 'Helvetica-Bold', fontWeight: 'bold' }],
});

Font.register({
  family: 'sans-serif',
  fonts: [{ src: 'Helvetica' }, { src: 'Helvetica-Bold', fontWeight: 'bold' }],
});

// --- HELPERS ---
const applyOpacityToHex = (hex, opacityPercent) => {
  if (!hex) return '#F5F5F5';
  const cleanHex = hex.replace('#', '');
  if (cleanHex.length !== 6) return hex;

  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  const opacity = Math.max(0, Math.min(100, opacityPercent)) / 100;

  // Blend with white background for solid PDF rendering
  const blendedR = Math.round(r * opacity + 255 * (1 - opacity));
  const blendedG = Math.round(g * opacity + 255 * (1 - opacity));
  const blendedB = Math.round(b * opacity + 255 * (1 - opacity));

  const toHex = (n) => n.toString(16).padStart(2, '0');
  return `#${toHex(blendedR)}${toHex(blendedG)}${toHex(blendedB)}`.toUpperCase();
};

const stripDefaultBlackColors = (html) => {
  if (!html) return '';
  return html.replace(
    /(^|[\s;"'])color\s*:\s*(?:#000000|#000|rgb\(\s*0\s*,\s*0\s*,\s*0\s*\)|rgba\(\s*0\s*,\s*0\s*,\s*0\s*,\s*(?:1|1\.0+)\s*\)|black|windowtext)\s*;?/gi,
    '$1',
  );
};

// Safely injects muted grey (#7F7F7F) into blocks without explicit non-black colors
const applyMutedGreyFallback = (htmlString) => {
  if (!htmlString) return '';
  return htmlString.replace(/<(p|li)([^>]*)>/gi, (match, tag, attributes) => {
    if (/(?:^|[\s;"'])color\s*:/i.test(attributes)) {
      return match;
    }
    if (attributes.toLowerCase().includes('style="')) {
      return `<${tag}${attributes.replace(/style="/i, 'style="color: #7F7F7F; ')}>`;
    }
    if (attributes.toLowerCase().includes("style='")) {
      return `<${tag}${attributes.replace(/style='/i, "style='color: #7F7F7F; ")}>`;
    }
    return `<${tag}${attributes} style="color: #7F7F7F;">`;
  });
};

// Ensures any loose/unwrapped text outside of block-level elements is wrapped in <p>...</p>
const wrapLooseTextInParagraphs = (htmlFragment) => {
  if (!htmlFragment || !htmlFragment.trim()) return '';

  const blockRegex =
    /(<(?:p|div|h[1-6]|ul|ol|table|blockquote)[\s>][\s\S]*?<\/(?:p|div|h[1-6]|ul|ol|table|blockquote)>)/gi;
  const parts = htmlFragment.split(blockRegex);

  return parts
    .map((part) => {
      if (!part || !part.trim()) return '';
      if (/^<(?:p|div|h[1-6]|ul|ol|table|blockquote)[\s>]/i.test(part.trim())) {
        return part;
      }
      const lines = part
        .split(/<br\s*\/?>|\r?\n/i)
        .map((l) => l.trim())
        .filter(Boolean);
      return lines.map((l) => `<p>${l}</p>`).join('');
    })
    .join('');
};

// Formats the description so that:
// 1. The first paragraph (title) keeps its RTE colour or defaults to #000000 (black).
// 2. If the first paragraph contains <br> tags (Shift+Enter / plain-text newlines),
//    only the first line is the black title and subsequent lines become grey body text.
// 3. Any loose/unwrapped text after the title <p> is wrapped in <p> tags.
// 4. Body paragraphs and list items have default black inline styles stripped so they
//    reliably receive #7F7F7F unless a non-black colour was explicitly assigned in RTE.
const formatDescriptionColors = (rawHtml) => {
  if (!rawHtml) return '';

  let processed = rawHtml
    .replace(/\u00A0/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .trim();

  // Strip out empty paragraphs at the start that intercept the title logic
  processed = processed.replace(/^(<p[^>]*>(?:&nbsp;|\s|<br\s*\/?>)*<\/p>\s*)+/i, '');

  // If the description does not start with a block tag, wrap loose text in <p> first
  if (!/^<(p|div|h[1-6]|ul|ol|li|table|blockquote)[\s>]/i.test(processed)) {
    processed = wrapLooseTextInParagraphs(processed);
  }

  // Match the first <p>...</p> as the title paragraph
  const firstPMatch = processed.match(/^(\s*<p([^>]*)>([\s\S]*?)<\/p>)/i);
  if (firstPMatch) {
    const fullFirstP = firstPMatch[1];
    const firstPAttrs = firstPMatch[2] || '';
    const firstPInner = firstPMatch[3] || '';
    let remainder = processed.slice(fullFirstP.length);

    let titleInner = firstPInner;
    const brSplitMatch = firstPInner.match(/^(.*?)<br\s*\/?>([\s\S]*)$/i);
    if (brSplitMatch && brSplitMatch[2].replace(/<[^>]+>/g, '').trim().length > 0) {
      titleInner = brSplitMatch[1];
      remainder = `<p>${brSplitMatch[2]}</p>` + remainder;
    }

    // Wrap any loose/unwrapped text in remainder (such as raw text after the title </p>) in <p> tags
    remainder = wrapLooseTextInParagraphs(remainder);

    // Check if an explicit colour is already set on the first <p> or inside it
    const hasExplicitTitleColor =
      /(?:^|[\s;"'])color\s*:/i.test(firstPAttrs) ||
      /(?:^|[\s;"'])color\s*:/i.test(titleInner);

    let newFirstPAttrs = firstPAttrs;
    if (!hasExplicitTitleColor) {
      if (/style="/i.test(newFirstPAttrs)) {
        newFirstPAttrs = newFirstPAttrs.replace(/style="/i, 'style="color: #000000; ');
      } else if (/style='/i.test(newFirstPAttrs)) {
        newFirstPAttrs = newFirstPAttrs.replace(/style='/i, "style='color: #000000; ");
      } else {
        newFirstPAttrs += ' style="color: #000000;"';
      }
    }

    const formattedFirstP = `<p${newFirstPAttrs}>${titleInner}</p>`;
    const cleanedRemainder = applyMutedGreyFallback(stripDefaultBlackColors(remainder));
    return formattedFirstP + cleanedRemainder;
  }

  return applyMutedGreyFallback(
    stripDefaultBlackColors(wrapLooseTextInParagraphs(processed)),
  );
};

// Estimates the rendered vertical height (in points) of a description block inside the text column
// to determine whether the entire item can fit on a single A4 page.
const estimateRowHeightPt = (html, standardSize, colWidthPt, photoSizePt, isHilight) => {
  // A4 width is 595.28pt, minus 72pt horizontal page padding = 523.28pt content width
  const textColWidthPt = Math.max(150, 523.28 - colWidthPt);
  // Use 0.58 factor to account for word-wrap slack at the end of lines and bold/capital characters
  const avgCharWidthPt = standardSize * 0.58;
  const charsPerLine = Math.max(20, Math.floor(textColWidthPt / avgCharWidthPt));

  const blockMatches = [...html.matchAll(/<(p|li|h[1-6])([^>]*)>([\s\S]*?)<\/\1>/gi)];

  let totalTextHeightPt = 0;

  if (blockMatches.length > 0) {
    blockMatches.forEach((m) => {
      const tag = (m[1] || 'p').toLowerCase();
      const attrs = m[2] || '';
      const inner = m[3] || '';

      // Detect if this block has an enlarged inline font-size
      let blockFontSize = standardSize;
      const sizeMatches = [
        ...attrs.matchAll(/font-size:\s*(\d+(?:\.\d+)?)(?:pt|px)/gi),
        ...inner.matchAll(/font-size:\s*(\d+(?:\.\d+)?)(?:pt|px)/gi),
      ];
      if (sizeMatches.length > 0) {
        blockFontSize = Math.max(
          standardSize,
          ...sizeMatches.map((sm) => parseFloat(sm[1])),
        );
      }

      const blockCharsPerLine =
        tag === 'li'
          ? Math.max(15, Math.floor((textColWidthPt - 20) / (blockFontSize * 0.58)))
          : Math.max(20, Math.floor(textColWidthPt / (blockFontSize * 0.58)));

      const blockLineHeightPt = blockFontSize * 1.28;

      const subLines = inner.split(/<br\s*\/?>/i);
      let blockLines = 0;
      subLines.forEach((sl) => {
        const plain = sl
          .replace(/<[^>]+>/g, '')
          .replace(/\s+/g, ' ')
          .trim();
        blockLines += Math.max(1, Math.ceil(plain.length / blockCharsPerLine));
      });
      // Each <p> or <li> has a 3pt bottom margin
      totalTextHeightPt += blockLines * blockLineHeightPt + 3.5;
    });
  } else {
    const plain = html
      .replace(/<[^>]+>/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    const lines = Math.max(1, Math.ceil(plain.length / charsPerLine));
    totalTextHeightPt = lines * (standardSize * 1.28);
  }

  const hilightPaddingPt = isHilight ? 24 : 0;
  return Math.max(photoSizePt, totalTextHeightPt) + hilightPaddingPt + 8;
};

const PortfolioDocument = ({ data }) => {
  const {
    title,
    subjectTitle,
    regionText,
    categoryText,
    dateText,
    batchText,
    keyword,
    infoFilterA,
    infoFilterB,
    logo,
    photoDimension,
    items,
    useRoundedCorners,
    roundedCornerRadius,
    isMultipleRegions,
    hilightColor,
    hilightOpacity,
  } = data;

  const displayTitle = subjectTitle || title || '';

  // Extract dynamic font settings from incoming payload
  const STANDARD_FONT = data.defaultFontType || 'Arial';
  const STANDARD_SIZE =
    parseInt((data.defaultFontSize || '11pt').replace(/[^0-9]/g, '')) || 11;

  const finalHilightColor = applyOpacityToHex(
    hilightColor || '#F5F5F5',
    hilightOpacity !== undefined ? hilightOpacity : 100,
  );

  // Moved stylesheets inside component to bind to dynamic fonts
  const styles = StyleSheet.create({
    page: {
      paddingTop: 36,
      paddingBottom: 36,
      paddingLeft: 36,
      paddingRight: 36,
      fontFamily: STANDARD_FONT,
      fontSize: STANDARD_SIZE,
    },
    headerContainer: {
      flexDirection: 'row',
      borderBottomWidth: 1.5,
      borderBottomColor: '#000000',
      paddingBottom: 6,
      marginBottom: 6,
    },
    headerLeft: {
      width: 175,
      justifyContent: 'flex-start',
    },
    headerRight: {
      flex: 1,
      justifyContent: 'flex-start',
      alignItems: 'stretch',
      paddingLeft: 30,
    },
    logo: {
      width: 170,
      objectFit: 'contain',
      marginBottom: 6,
    },
    title: {
      fontSize: 16,
      fontWeight: 'bold',
      marginBottom: 3,
    },
    settingsRow: {
      flexDirection: 'row',
      marginBottom: 0,
      width: '100%',
    },
    settingsLabel: {
      width: 50,
      fontSize: 7,
      fontWeight: 'bold',
    },
    settingsValue: {
      flex: 1,
      fontSize: 7,
      flexWrap: 'wrap',
    },
    spacer: {
      height: 6,
    },
    row: {
      flexDirection: 'row',
      // AMENDED: Removed paddingTop and paddingBottom to prevent invisible spacing on normal rows
    },
    imageCol: {
      paddingRight: 20,
    },
    textCol: {
      flex: 1,
      justifyContent: 'flex-start',
    },
    photo: {
      objectFit: 'cover',
    },
    noImageText: {
      color: '#999999',
      fontStyle: 'italic',
    },
    listItemRow: {
      flexDirection: 'row',
      marginBottom: 3,
    },
    bullet: {
      width: 10,
      fontSize: STANDARD_SIZE,
      fontFamily: STANDARD_FONT,
      color: '#7F7F7F',
    },
    listItemContent: {
      flex: 1,
      fontSize: STANDARD_SIZE,
      lineHeight: 1.1,
      fontFamily: STANDARD_FONT,
      color: '#7F7F7F',
    },
    pageNumber: {
      position: 'absolute',
      fontSize: 9,
      bottom: 18,
      right: 36,
      fontFamily: STANDARD_FONT,
    },
  });

  const htmlStyles = {
    body: {
      fontFamily: STANDARD_FONT,
      fontSize: STANDARD_SIZE,
      lineHeight: 1.2,
      color: '#7F7F7F',
    },
    p: {
      fontFamily: STANDARD_FONT,
      fontSize: STANDARD_SIZE,
      marginBottom: 3,
      marginTop: 0,
      lineHeight: 1.2,
    },
    span: { fontFamily: STANDARD_FONT, lineHeight: 1.2 },
    div: { fontFamily: STANDARD_FONT, fontSize: STANDARD_SIZE, lineHeight: 1.2 },
    h1: {
      fontFamily: STANDARD_FONT,
      fontSize: STANDARD_SIZE,
      fontWeight: 'bold',
      marginBottom: 3,
      marginTop: 0,
      lineHeight: 1.2,
    },
    h2: {
      fontFamily: STANDARD_FONT,
      fontSize: STANDARD_SIZE,
      fontWeight: 'bold',
      marginBottom: 3,
      marginTop: 0,
      lineHeight: 1.2,
    },
    h3: {
      fontFamily: STANDARD_FONT,
      fontSize: STANDARD_SIZE,
      fontWeight: 'bold',
      marginBottom: 3,
      marginTop: 0,
      lineHeight: 1.2,
    },
    h4: {
      fontFamily: STANDARD_FONT,
      fontSize: STANDARD_SIZE,
      fontWeight: 'bold',
      marginBottom: 3,
      marginTop: 0,
      lineHeight: 1.2,
    },
    h5: {
      fontFamily: STANDARD_FONT,
      fontSize: STANDARD_SIZE,
      fontWeight: 'bold',
      marginBottom: 3,
      marginTop: 0,
      lineHeight: 1.2,
    },
    h6: {
      fontFamily: STANDARD_FONT,
      fontSize: STANDARD_SIZE,
      fontWeight: 'bold',
      marginBottom: 3,
      marginTop: 0,
      lineHeight: 1.2,
    },
    b: { fontFamily: STANDARD_FONT, fontWeight: 'bold' },
    strong: { fontFamily: STANDARD_FONT, fontWeight: 'bold' },
    i: { fontFamily: STANDARD_FONT, fontStyle: 'italic' },
    em: { fontFamily: STANDARD_FONT, fontStyle: 'italic' },
    u: {
      fontFamily: STANDARD_FONT,
      textDecoration: 'underline',
    },
    a: { fontFamily: STANDARD_FONT, textDecoration: 'none' },
    ul: { marginLeft: 10, marginBottom: 0, marginTop: 0 },
    ol: { marginLeft: 10, marginBottom: 0, marginTop: 0 },
    li: {
      fontFamily: STANDARD_FONT,
      fontSize: STANDARD_SIZE,
      marginBottom: 0,
      color: '#7F7F7F',
    },
    table: { fontSize: STANDARD_SIZE },
    tr: { fontSize: STANDARD_SIZE },
    td: { fontSize: STANDARD_SIZE },
    th: { fontSize: STANDARD_SIZE },
    tbody: { fontSize: STANDARD_SIZE },
    thead: { fontSize: STANDARD_SIZE },
    '.indent-1': { marginLeft: 40 },
    '.indent-2': { marginLeft: 80 },
    '.indent-3': { marginLeft: 120 },
    '.indent-4': { marginLeft: 160 },
    '.ql-size-small': { fontSize: STANDARD_SIZE },
    '.ql-size-large': { fontSize: STANDARD_SIZE },
    '.ql-size-huge': { fontSize: STANDARD_SIZE },
  };

  const photoSizeMm = parseInt(photoDimension) || 50;
  const photoSizePt = photoSizeMm * 2.835;
  const colWidthPt = photoSizePt + 20;

  // Prepare valid items and inject required rendering state
  const validItems = [];
  for (const item of items) {
    const firstMedia = item.photos && item.photos.length > 0 ? item.photos[0] : null;
    const firstImageMedia = (item.photos || []).find((p) => {
      if (p.type) return p.type === 'image';
      const str = (p.fileName || p.url || '').toLowerCase();
      return !str.match(/\.(mp4|mp3|wav|m4a|aac|mov|avi|webm)$/i);
    });

    const imageUrl = firstImageMedia ? firstImageMedia.url : null;
    let rawText = '';
    if (firstImageMedia && firstImageMedia.description) {
      rawText = firstImageMedia.description;
    } else if (firstMedia && firstMedia.description) {
      rawText = firstMedia.description;
    }

    const strippedText = rawText
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/gi, '')
      .trim();
    const hasText = strippedText.length > 0;

    if (imageUrl || hasText) {
      validItems.push({
        ...item,
        _imageUrl: imageUrl,
        _rawText: rawText,
        _hasText: hasText,
      });
    }
  }

  // Grouping by Region Name
  const groupedItems = {};
  if (isMultipleRegions) {
    validItems.forEach((item) => {
      const rName = item.regionName || 'Other';
      if (!groupedItems[rName]) groupedItems[rName] = [];
      groupedItems[rName].push(item);
    });
  } else {
    groupedItems['default'] = validItems;
  }

  const sortedRegionNames = isMultipleRegions
    ? Object.keys(groupedItems).sort((a, b) => a.localeCompare(b))
    : ['default'];

  const showRegionHeaders = isMultipleRegions && sortedRegionNames.length > 1;
  let globalItemCount = 0;

  // Safe maximum height for a single page (A4 841.89pt - 72pt top/bottom padding - footer/header safety margin)
  const SINGLE_PAGE_MAX_HEIGHT_PT = 680;

  return (
    <Document>
      <Page size='A4' style={styles.page}>
        <View style={styles.headerContainer}>
          <View style={styles.headerLeft}>
            {logo ? (
              <Image src={logo} style={styles.logo} />
            ) : (
              <Text style={[styles.noImageText, { marginBottom: 6 }]}>[LOGO]</Text>
            )}
          </View>

          <View style={styles.headerRight}>
            <Text style={styles.title}>{displayTitle}</Text>

            <View style={styles.settingsRow}>
              <Text style={styles.settingsLabel}>Region:</Text>
              <Text style={styles.settingsValue}>{regionText}</Text>
            </View>

            <View style={styles.settingsRow}>
              <Text style={styles.settingsLabel}>Category:</Text>
              <Text style={styles.settingsValue}>{categoryText}</Text>
            </View>

            <View style={styles.settingsRow}>
              <Text style={styles.settingsLabel}>Date:</Text>
              <Text style={styles.settingsValue}>{dateText}</Text>
            </View>

            {batchText ? (
              <View style={styles.settingsRow}>
                <Text style={styles.settingsLabel}>Batch:</Text>
                <Text style={styles.settingsValue}>{batchText}</Text>
              </View>
            ) : null}

            {keyword ? (
              <View style={styles.settingsRow}>
                <Text style={styles.settingsLabel}>Keyword:</Text>
                <Text style={styles.settingsValue}>{keyword}</Text>
              </View>
            ) : null}

            {infoFilterA || infoFilterB ? (
              <React.Fragment>
                {infoFilterA && infoFilterB ? (
                  <React.Fragment>
                    <View style={styles.settingsRow}>
                      <Text style={styles.settingsLabel}>Info Filter:</Text>
                      <Text style={styles.settingsValue}>{infoFilterA}</Text>
                    </View>
                    <View style={styles.settingsRow}>
                      <Text style={styles.settingsLabel}></Text>
                      <Text style={styles.settingsValue}>AND</Text>
                    </View>
                    <View style={styles.settingsRow}>
                      <Text style={styles.settingsLabel}></Text>
                      <Text style={styles.settingsValue}>{infoFilterB}</Text>
                    </View>
                  </React.Fragment>
                ) : (
                  <View style={styles.settingsRow}>
                    <Text style={styles.settingsLabel}>Info Filter:</Text>
                    <Text style={styles.settingsValue}>{infoFilterA || infoFilterB}</Text>
                  </View>
                )}
              </React.Fragment>
            ) : null}
          </View>
        </View>

        <View style={styles.spacer} />

        {sortedRegionNames.map((regionName) => {
          const regionItems = groupedItems[regionName];
          return (
            <React.Fragment key={regionName}>
              {showRegionHeaders && (
                <View
                  minPresenceAhead={photoSizePt + 40}
                  style={{ marginTop: 12, marginBottom: 6 }}>
                  <Text
                    style={{
                      fontFamily: STANDARD_FONT,
                      fontSize: 12,
                      fontWeight: 'bold',
                      color: '#000000',
                    }}>
                    {regionName}
                  </Text>
                </View>
              )}

              {regionItems.map((item, index) => {
                const imageUrl = item._imageUrl;
                const rawText = item._rawText;
                const hasText = item._hasText;
                const isHilight = item.hilight === true || item.Hilight === true;

                let targetDesc =
                  '<p style="color: #7F7F7F;">No description provided.</p>';
                if (hasText) {
                  targetDesc = formatDescriptionColors(rawText);
                }

                let rawDesc = targetDesc.replace(/list-style-type:[^;"]*;?/gi, '');

                // Retain inline font selections by replacing font-family ONLY if no inline font is set
                // React-PDF will inherit from htmlStyles.body if font-family is stripped or missing.
                rawDesc = rawDesc.replace(
                  /font-family:[^;"]*;?/gi,
                  `font-family: ${STANDARD_FONT};`,
                );

                // Expand parent <p> font-size ONLY when a child <span> has an explicit font-size,
                // and preserve line-height: 1.2 so react-pdf-html does not collapse line spacing.
                rawDesc = rawDesc.replace(
                  /<p([^>]*)>([\s\S]*?)<\/p>/gi,
                  (match, pAttrs, innerHtml) => {
                    const sizeMatches = [
                      ...innerHtml.matchAll(/font-size:\s*(\d+(?:\.\d+)?)pt/gi),
                    ];
                    if (sizeMatches.length === 0) {
                      return `<p${pAttrs}>${innerHtml}</p>`;
                    }
                    const maxPt = Math.max(
                      STANDARD_SIZE,
                      ...sizeMatches.map((m) => parseFloat(m[1])),
                    );
                    let newAttrs = pAttrs;
                    if (!/font-size:/i.test(newAttrs)) {
                      if (/style="/i.test(newAttrs)) {
                        newAttrs = newAttrs.replace(
                          /style="/i,
                          `style="font-size: ${maxPt}pt; line-height: 1.2; `,
                        );
                      } else if (/style='/i.test(newAttrs)) {
                        newAttrs = newAttrs.replace(
                          /style='/i,
                          `style='font-size: ${maxPt}pt; line-height: 1.2; `,
                        );
                      } else {
                        newAttrs += ` style="font-size: ${maxPt}pt; line-height: 1.2;"`;
                      }
                    }
                    return `<p${newAttrs}>${innerHtml}</p>`;
                  },
                );

                // Convert pt to px to ensure react-pdf-html parses the inline size correctly
                rawDesc = rawDesc.replace(
                  /font-size:\s*(\d+(?:\.\d+)?)pt/gi,
                  'font-size: $1px',
                );

                rawDesc = rawDesc.replace(/class="ql-size-[^"]*"/gi, '');
                rawDesc = rawDesc.replace(/<\/?font[^>]*>/gi, '');

                // Estimate whether this item physically exceeds the height of a single full A4 page.
                // - If it fits on a single page (exceedsOnePage === false), wrap is strictly false,
                //   so React-PDF keeps the entire item intact and starts it on a new page if it won't fit at the bottom.
                // - If it is taller than a single page (exceedsOnePage === true), we start it on a new page (if not the first item)
                //   and allow it to wrap across pages so it does not crush/overwrite itself.
                const estimatedHeightPt = estimateRowHeightPt(
                  rawDesc,
                  STANDARD_SIZE,
                  colWidthPt,
                  photoSizePt,
                  isHilight,
                );
                const exceedsOnePage = estimatedHeightPt > SINGLE_PAGE_MAX_HEIGHT_PT;

                const isFirstGlobalItem = globalItemCount === 0;
                const isEven = globalItemCount % 2 === 0;
                globalItemCount++;

                const photoStyles = [
                  styles.photo,
                  { width: photoSizePt, height: photoSizePt },
                ];
                if (useRoundedCorners && roundedCornerRadius) {
                  photoStyles.push({ borderRadius: roundedCornerRadius });
                }

                // AMENDED: Added uniform row spacing strictly governed by margin
                const rowStyles = [styles.row, { marginBottom: 8 }];
                if (isHilight) {
                  rowStyles.push({
                    backgroundColor: finalHilightColor,
                    border: '1px solid #CCCCCC',
                    paddingTop: 10,
                    paddingBottom: 10,
                    paddingLeft: 10,
                    paddingRight: 10,
                    marginLeft: -10, // AMENDED: Extend outward left
                    marginRight: -10, // AMENDED: Extend outward right
                    marginTop: 0,
                    marginBottom: 8,
                    borderRadius:
                      useRoundedCorners && roundedCornerRadius ? roundedCornerRadius : 0,
                  });
                }

                return (
                  <React.Fragment key={`${regionName}-${index}`}>
                    <View
                      style={rowStyles}
                      wrap={exceedsOnePage}
                      break={exceedsOnePage && !isFirstGlobalItem}>
                      {isEven ? (
                        <>
                          <View style={[styles.imageCol, { width: colWidthPt }]}>
                            {imageUrl ? (
                              <Image src={imageUrl} style={photoStyles} />
                            ) : null}
                          </View>

                          <View style={styles.textCol}>
                            <Html
                              style={{
                                fontSize: STANDARD_SIZE,
                                fontFamily: STANDARD_FONT,
                                color: '#7F7F7F',
                              }}
                              stylesheet={htmlStyles}
                              renderComponent={({ node, children }) => {
                                if (node.tagName === 'li') {
                                  return (
                                    <View style={styles.listItemRow}>
                                      <Text style={styles.bullet}>•</Text>
                                      <Text style={styles.listItemContent}>
                                        {children}
                                      </Text>
                                    </View>
                                  );
                                }
                              }}>
                              {rawDesc}
                            </Html>
                          </View>
                        </>
                      ) : (
                        <>
                          <View style={[styles.textCol, { paddingRight: 20 }]}>
                            <Html
                              style={{
                                fontSize: STANDARD_SIZE,
                                fontFamily: STANDARD_FONT,
                                color: '#7F7F7F',
                              }}
                              stylesheet={htmlStyles}
                              renderComponent={({ node, children }) => {
                                if (node.tagName === 'li') {
                                  return (
                                    <View style={styles.listItemRow}>
                                      <Text style={styles.bullet}>•</Text>
                                      <Text style={styles.listItemContent}>
                                        {children}
                                      </Text>
                                    </View>
                                  );
                                }
                              }}>
                              {rawDesc}
                            </Html>
                          </View>

                          <View style={{ width: photoSizePt }}>
                            {imageUrl ? (
                              <Image src={imageUrl} style={photoStyles} />
                            ) : null}
                          </View>
                        </>
                      )}
                    </View>
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          );
        })}

        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          fixed
        />
      </Page>
    </Document>
  );
};

export const generatePortfolioPDF = async (data) => {
  try {
    const blob = await pdf(<PortfolioDocument data={data} />).toBlob();
    saveAs(blob, `${data.title || 'Portfolio'}.pdf`);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

export const getPortfolioPDFBlob = async (data) => {
  try {
    const blob = await pdf(<PortfolioDocument data={data} />).toBlob();
    return blob;
  } catch (error) {
    console.error('Error generating PDF Blob:', error);
    throw error;
  }
};

// AMENDED: Accept dynamic scale and quality to facilitate multiple size outputs
export const generatePdfThumbnail = async (pdfBlob, scale = 1.5, quality = 0.8) => {
  try {
    const arrayBuffer = await pdfBlob.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);

    const loadingTask = pdfjsLib.getDocument({
      data: uint8Array,
      cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/cmaps/`,
      cMapPacked: true,
      standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/standard_fonts/`,
    });

    const pdf = await loadingTask.promise;
    const page = await pdf.getPage(1);

    const viewport = page.getViewport({ scale: scale });

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.height = viewport.height;
    canvas.width = viewport.width;

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    const renderContext = {
      canvasContext: context,
      viewport: viewport,
    };

    await page.render(renderContext).promise;

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Canvas failed to export blob.'));
            return;
          }
          resolve(blob);
        },
        'image/jpeg',
        quality,
      );
    });
  } catch (error) {
    console.error('Error generating PDF thumbnail:', error);
    throw error;
  }
};
